import { defineStore } from "pinia";
import { User, UserForm } from "@/common/models/user";
import {
  editInfoUser,
  fetchMe,
  updateUserNotificationSettings,
} from "@/common/services/user.service";
import { logout } from "@/common/services/auth.service";
import { useErrorStore } from "@/common/store/error.store";
import { useGroupsStore } from "@/common/store/groups.store";
import { useCompetitionsStore } from "@/common/store/competitions.store";
import {
  clearPushPromptDismissedState,
  clearPushNotificationsForUser,
  getPushNotificationsAvailability,
  hasPushPromptDismissedState,
  initializePushNotifications,
  requestPushEnablePrompt,
} from "@/common/services/notifications.service";

interface UserState {
  user: User | null;
  hasReconciledPushOnAppEntry: boolean;
  hasShownLoginPushPrompt: boolean;
}

export const useUserStore = defineStore("user", {
  state: (): UserState => ({
    user: null,
    hasReconciledPushOnAppEntry: false,
    hasShownLoginPushPrompt: false,
  }),

  getters: {
    currentUser(): User | null {
      return this.user
        ? {
            ...this.user,
            groups: useGroupsStore().all,
          }
        : null;
    },
  },

  actions: {
    async fetchCurrentUser(options: { reconcilePushOnAppEntry?: boolean } = {}): Promise<void> {
      const competitionStore = useCompetitionsStore();
      try {
        const fetchedUser = await fetchMe();
        const shouldReconcilePushOnAppEntry =
          options.reconcilePushOnAppEntry && !this.hasReconciledPushOnAppEntry;
        const user = shouldReconcilePushOnAppEntry
          ? await this.reconcilePushNotificationsOnAppEntry(fetchedUser)
          : fetchedUser;

        if (shouldReconcilePushOnAppEntry) {
          this.hasReconciledPushOnAppEntry = true;
          this.maybeShowLoginPushPrompt(user);
        }

        await competitionStore.fetchCompetitions();
        await useGroupsStore().fetchGroups();
        this.fetchUserSuccess(user);
      } catch (error) {
        this.fetchUserError(error);
      }
    },

    fetchUserSuccess(user: User): void {
      this.user = user;

      if (!this.user.settings?.notifications?.push) {
        clearPushNotificationsForUser(this.user.id).catch((error) => {
          console.error("Error clearing push notifications after settings update:", error);
        });
        return;
      }

      if (!("Notification" in window)) {
        return;
      }

      if (Notification.permission === "granted") {
        initializePushNotifications(user.id).catch((error) => {
          console.error("Error inicializando notificaciones push:", error);
        });
      }
    },

    fetchUserError(err: any): void {
      useErrorStore().throwError(`fetchUserError: ${err.toString()}`);
    },

    async editInfoUser(request: UserForm): Promise<void> {
      try {
        const user = await editInfoUser(request);
        this.editInfoUserSuccess(user);
      } catch (error) {
        this.editInfoUserError(error);
      }
    },

    editInfoUserSuccess(user: Partial<User>): void {
      this.user = Object.assign({}, this.user, user);
    },

    editInfoUserError(err: any): void {
      useErrorStore().throwError(`editInfoUserError: ${err.toString()}`);
    },

    maybeShowLoginPushPrompt(user: User): void {
      if (this.hasShownLoginPushPrompt) {
        return;
      }

      if (user.settings?.notifications?.push) {
        return;
      }

      if (typeof window === "undefined" || !("Notification" in window)) {
        return;
      }

      if (Notification.permission === "denied") {
        return;
      }

      if (!getPushNotificationsAvailability().supported) {
        return;
      }

      if (hasPushPromptDismissedState(user.id)) {
        return;
      }

      this.hasShownLoginPushPrompt = true;
      requestPushEnablePrompt(user.id).catch((error) => {
        console.error("Error showing login push prompt:", error);
      });
    },

    async reconcilePushNotificationsOnAppEntry(user: User): Promise<User> {
      if (typeof window === "undefined" || !user.settings?.notifications?.push) {
        return user;
      }

      try {
        const hasNotificationPermission =
          "Notification" in window && Notification.permission === "granted";

        if (!hasNotificationPermission) {
          return await this.disablePushNotificationsAfterReconciliationFailure(user);
        }

        const initialized = await initializePushNotifications(user.id, {
          requestPermission: false,
        });

        if (initialized) {
          return user;
        }

        return await this.disablePushNotificationsAfterReconciliationFailure(user);
      } catch (error) {
        console.error("Error reconciling push notifications on app entry:", error);
        return user;
      }
    },

    async disablePushNotificationsAfterReconciliationFailure(user: User): Promise<User> {
      await clearPushNotificationsForUser(user.id);
      return updateUserNotificationSettings({
        ...user.settings.notifications,
        push: false,
      });
    },

    async enablePushNotifications(): Promise<boolean> {
      if (!this.user?.id) {
        return false;
      }

      clearPushPromptDismissedState(this.user.id);

      try {
        const initialized = await initializePushNotifications(this.user.id, {
          requestPermission: true,
        });
        if (!initialized) {
          return false;
        }

        const user = await updateUserNotificationSettings({
          ...this.user.settings.notifications,
          push: true,
        });
        this.fetchUserSuccess(user);
        return true;
      } catch (err: any) {
        await clearPushNotificationsForUser(this.user.id);
        useErrorStore().throwError(`enablePushNotificationsError: ${err.toString()}`);
        throw err;
      }
    },

    async disablePushNotifications(): Promise<void> {
      if (!this.user?.id) {
        return;
      }

      try {
        const user = await updateUserNotificationSettings({
          ...this.user.settings.notifications,
          push: false,
        });
        this.fetchUserSuccess(user);
      } catch (err: any) {
        useErrorStore().throwError(`disablePushNotificationsError: ${err.toString()}`);
        throw err;
      }
    },

    async logOut(): Promise<void> {
      if (this.user?.id) {
        await clearPushNotificationsForUser(this.user.id);
      }
      await logout();
      this.user = null;
      this.hasReconciledPushOnAppEntry = false;
      this.hasShownLoginPushPrompt = false;
    },
  },
});
